import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import type { EntryComponent } from 'cms/entries/entries';
import type { AddressSearchLocation } from 'components/globals';
import NewSellerFlowAddressInput from 'components/shared/NewSellerFlowAddressInput';
import type { ICompareHero } from 'declarations/contentful';
import { optimizeContentfulImageUrl } from 'helpers/imageUrl';

const RenderCompareHero = (entry: ICompareHero) => {
  const { fields } = entry;
  const heroImageUrl = fields.heroImage?.fields?.file?.url;
  const optimizedHeroImageUrl = heroImageUrl
    ? optimizeContentfulImageUrl(heroImageUrl, { width: 960, quality: 80 })
    : undefined;

  return (
    <Col as="section" className="items-center bg-od-bg-primary py-20 max-lg:py-10 max-md:py-12">
      <Box className="block w-full max-w-280 px-6">
        <Col className="items-center gap-8 md:flex-row">
          {/* Text column is intentionally wider than the image column (Bricks flex 1.1 vs 0.9). */}
          <Col className="flex-[1.1] gap-5">
            <Text as="span" className="text-label-sm text-od-content-accent-brand">
              {fields.eyebrow}
            </Text>
            <Col className="gap-5">
              <Text
                as="h1"
                className="text-header-lg text-od-content-primary max-lg:text-header-md max-md:text-header-sm"
              >
                {fields.title}{' '}
                {fields.titleAccent && (
                  <Text
                    as="span"
                    className="text-header-lg text-od-content-accent-brand max-lg:text-header-md max-md:text-header-sm"
                  >
                    {fields.titleAccent}
                  </Text>
                )}
              </Text>
              {/* leading-[1.6] preserves the original's hand-tuned line-height; the
                  text-body-lg token's intrinsic 1.45 would otherwise change spacing. */}
              <Text
                as="p"
                className="max-w-154 text-body-lg leading-[1.6] text-od-content-secondary"
              >
                {fields.description}
              </Text>
            </Col>
            <Col className="gap-5">
              <Box className="w-full max-w-129">
                <NewSellerFlowAddressInput
                  hideLabel
                  inputLocation={
                    (fields.addressInputLocation || 'hero-cta') as AddressSearchLocation
                  }
                  analyticsPrefix={fields.addressInputAnalyticsPrefix || 'compare-hero'}
                  trackingTaxonomy="new_homepage"
                  placeholder={fields.addressInputPlaceholder || 'Enter your home address'}
                  channel="DEFAULT"
                  size="medium"
                />
              </Box>
              {fields.trustBadges && fields.trustBadges.length > 0 && (
                <Row className="flex-wrap gap-6">
                  {fields.trustBadges.map((item) => (
                    <Row key={item} className="items-center gap-2">
                      <IconCircleCheckFilled
                        size={18}
                        className="text-od-content-success"
                        aria-hidden="true"
                      />
                      <Text as="span" className="text-body-sm text-od-content-secondary">
                        {item}
                      </Text>
                    </Row>
                  ))}
                </Row>
              )}
            </Col>
          </Col>

          {fields.heroImage && (
            <Col className="flex-[0.9] items-center justify-center max-lg:hidden">
              <Image
                src={optimizedHeroImageUrl}
                alt={fields.heroImageAlt || ''}
                className="h-120 w-full max-w-120 rounded-2xl object-cover"
              />
            </Col>
          )}
        </Col>
      </Box>
    </Col>
  );
};

const CompareHero: EntryComponent<ICompareHero> = {
  render: RenderCompareHero,
};

export default CompareHero;
